import { useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { isDesignId } from '../appearance/designs.js';
import { writeDesign } from '../appearance/designStore.js';
import i18n from '../i18n.js';
import { isInterfaceLanguage, storeUserLanguage } from './languageStorage.js';

/**
 * Liens de présentation `?design=collection&lang=en` : le choix valide est
 * adopté pour le compte connecté seulement, puis les deux paramètres sont
 * retirés en UNE navigation du routeur (`replace`), qui garde le chemin, les
 * autres paramètres (`onglet`, `etat`…) et le fragment (`#demande-…`).
 *
 * Par le routeur et non par `history.replaceState` : sinon ses searchParams
 * gardent l'ancienne valeur, qu'un filtre de page remet dans l'URL et qui
 * annule le choix manuel suivant. Une seule navigation pour les deux
 * paramètres : deux navigations calculées sur la même adresse se
 * réécriraient l'une l'autre. Un visiteur anonyme garde les paramètres
 * jusqu'à la connexion. Monté par la coquille, dans le routeur.
 *
 * Lot C : quand la console a verrouillé le thème de la galerie
 * (`designLibre` faux), `?design=` est retiré de l'adresse sans être adopté.
 */
export function useEntryParams(compteId: string | null, designLibre = true): void {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const design = params.get('design');
  const lang = params.get('lang');
  useLayoutEffect(() => {
    if (compteId === null || (design === null && lang === null)) return;
    if (designLibre && isDesignId(design)) writeDesign(compteId, design);
    if (isInterfaceLanguage(lang)) {
      storeUserLanguage(compteId, lang);
      void i18n.changeLanguage(lang);
    }
    const suivants = new URLSearchParams(location.search);
    suivants.delete('design');
    suivants.delete('lang');
    const search = suivants.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
      { replace: true, preventScrollReset: true, state: location.state as unknown },
    );
  }, [compteId, design, designLibre, lang, location, navigate]);
}
